import React, { useState, useCallback, useEffect } from 'react';
import { Colors, Spacing } from '_styles';
import { createPortal } from 'react-dom';

/**
 * Reusable overlay component that displays content at mouse position on hover.
 * Perfect for showing additional information like flight details, tooltips, etc.
 *
 * @example
 * <HoverOverlay
 *   isVisible={showOverlay}
 *   onMouseEnter={handleMouseEnter}
 *   onMouseLeave={handleMouseLeave}
 *   onMouseMove={handleMouseMove}
 *   position={mousePosition}
 *   title="Flight Details"
 * >
 *   <div>Flight information here...</div>
 * </HoverOverlay>
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Content to display in overlay
 * @param {boolean} props.isVisible - Whether overlay should be shown
 * @param {function} props.onMouseEnter - Handler for mouse enter event
 * @param {function} props.onMouseLeave - Handler for mouse leave event
 * @param {function} props.onMouseMove - Handler for mouse move event
 * @param {Object} props.position - Mouse position {x, y}
 * @param {string} [props.title] - Optional title for overlay header
 * @param {Object} [props.triggerStyle] - Additional styles for the trigger area
 * @param {Object} [props.overlayStyle] - Additional styles for overlay container
 * @param {number} [props.offsetX=10] - Horizontal offset for overlay from mouse position
 * @param {number} [props.offsetY=10] - Vertical offset for overlay from mouse position
 */
const HoverOverlay = ({
    children,
    isVisible,
    onMouseEnter,
    onMouseLeave,
    onMouseMove,
    position,
    title,
    triggerStyle = {},
    overlayStyle = {},
    offsetX = 10,
    offsetY = 10
}) => {
    const handleMouseEnter = useCallback((e) => {
        onMouseEnter?.(e);
    }, [onMouseEnter]);

    const handleMouseLeave = useCallback(() => {
        onMouseLeave?.();
    }, [onMouseLeave]);

    const handleMouseMove = useCallback((e) => {
        onMouseMove?.(e);
    }, [onMouseMove]);

    return (
        <>
            {/* Invisible trigger area - covers full area */}
            <div
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onMouseMove={handleMouseMove}
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    cursor: 'pointer',
                    zIndex: 1,
                    ...triggerStyle
                }}
            />

            {/* Overlay - rendered via portal to document body */}
            {isVisible && children && createPortal(
                <div
                    style={{
                        position: 'fixed',
                        left: (position?.x || 0) + offsetX,
                        top: (position?.y || 0) + offsetY,
                        backgroundColor: 'white',
                        border: `1px solid ${Colors.GRAY_LIGHT}`,
                        borderRadius: 8,
                        padding: Spacing.SCALE_8,
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        zIndex: 2147483647, // Maximum possible z-index
                        maxWidth: 250,
                        whiteSpace: 'pre-line',
                        fontSize: 12,
                        color: Colors.TEXT_DARK,
                        pointerEvents: 'none',
                        ...overlayStyle
                    }}
                >
                    {title && (
                        <div style={{ fontWeight: 'bold', marginBottom: 4, color: Colors.PRM_BLUE }}>
                            {title}
                        </div>
                    )}
                    {children}
                </div>,
                document.body
            )}
        </>
    );
};

export default HoverOverlay;
